import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Edit, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/utils/api';




export default function AdminAmenities() {
  const [amenities, setAmenities] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    icon: '',
    description: ''
  });

  useEffect(() => {
    fetchAmenities();
  }, []);

  const fetchAmenities = async () => {
    try {
      const response = await apiClient.get('/amenities');
      setAmenities(response.data);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        id: editingAmenity?.id || undefined
      };

      if (editingAmenity) {
        await apiClient.put(`/admin/amenities/${editingAmenity.id}`, payload);
        toast.success('Amenity updated successfully!');
      } else {
        await apiClient.post('/admin/amenities', payload);
        toast.success('Amenity created successfully!');
      }

      setIsModalOpen(false);
      resetForm();
      fetchAmenities();
    } catch (error) {
      toast.error('Failed to save amenity');
    }
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setFormData(amenity);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this amenity?')) {
      try {
        await apiClient.delete(`/admin/amenities/${id}`);
        toast.success('Amenity deleted successfully!');
        fetchAmenities();
      } catch (error) {
        toast.error('Failed to delete amenity');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      icon: '',
      description: ''
    });
    setEditingAmenity(null);
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4">
            <ArrowLeft size={20} className="mr-2" />
            Back to Dashboard
          </Link>
          <div className="flex justify-between items-center">
            <h1 className="text-3xl font-bold text-gray-900">Manage Amenities</h1>
            <Button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700">
              <Plus size={18} className="mr-2" />
              Add Amenity
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {amenities.map((amenity) => (
            <div key={amenity.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="text-4xl mb-3">{amenity.icon}</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{amenity.name}</h3>
              <p className="text-gray-600 text-sm mb-4">{amenity.description}</p>
              <div className="flex space-x-2">
                <Button onClick={() => handleEdit(amenity)} size="sm" variant="outline">
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
                <Button onClick={() => handleDelete(amenity.id)} size="sm" variant="destructive">
                  <Trash2 size={16} className="mr-1" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) resetForm(); setIsModalOpen(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAmenity ? 'Edit Amenity' : 'Add New Amenity'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Amenity Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="High-Speed WiFi"
                required
              />
            </div>
            <div>
              <Label>Icon (emoji or lucide icon name)</Label>
              <Input
                value={formData.icon}
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                placeholder="🌐 or Wifi"
                required
              />
              <p className="text-sm text-gray-500 mt-1">You can use emoji or icon name</p>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Fast and reliable internet connection"
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => { resetForm(); setIsModalOpen(false); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                <Save size={18} className="mr-2" />
                Save Amenity
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}